// Настройки (04 §9) — the switches that change what the pipeline does.
//
// Written through `set_app_setting`, never by updating the row, because the RPC writes the
// audit_log entry alongside the change. "Who turned the LLM on, and when" is a question with a
// real answer, and it stays that way only if there is exactly one path to writing here.
//
// Booleans get a switch, everything else a text box holding raw JSON. That is deliberate: an
// editor that guessed at a shape would eventually guess wrong and write a string where the
// pipeline reads an object, and a settings screen that can corrupt config is worse than one that
// makes the owner type carefully.

import { useState } from 'react';
import { useSetSetting, useSettings } from './useAdmin.js';

const TITLES: Record<string, string> = {
  llm_billing_confirmed: 'Генериране на досиета с изкуствен интелект (LLM)',
  llm_chain: 'Верига от AI доставчици и модели (приоритетен ред)',
  gemini_model: 'Модел на Google Gemini по подразбиране',
  reaudit_days: 'Интервал за повторен уебсайт одит (в дни)',
  registry_autoapply_confidence: 'Минимална сигурност за автоматичен ЕИК (%)',
  identity_retry_days: 'Повторно търсене в Търговския регистър (в дни)',
  places_grid_max_calls: 'Лимит на заявки към Google Places на цикъл',
  harvest_retry_days: 'Опресняване на данни от каталози (в дни)',
  discovery_max_queries_per_run: 'Максимум заявки за търсене при един цикъл',
  discovery_tier_cooldowns: 'Пауза между пусканията по нива на заявките (в дни)',
};

/** What each key actually does, in the owner's language. A key name is not documentation. */
const EXPLAIN: Record<string, string> = {
  llm_billing_confirmed:
    'Докато е изключено, никакъв текст не се генерира с изкуствен интелект — досиетата остават машинни шаблони.',
  llm_chain:
    'Редът на AI моделите за текстовете: локалният Gemini CLI (Ultra) е основен (#1), следван от уеб модели (Groq, Cloudflare, Z.ai, Gemini Web) при отказ или квота. Важи от следващото пускане.',
  gemini_model:
    'Кой Gemini модел се ползва, когато редът gemini в llm_chain е включен. Промяната важи от следващото пускане на пайплайна.',
  reaudit_days:
    'След колко дни уебсайт одитът се счита за остарял и се обновява автоматично при нощния цикъл.',
  registry_autoapply_confidence:
    'Минимален процент сигурност (по подразбиране 80) за автоматично записване на намерен ЕИК без ръчна проверка.',
  identity_retry_days:
    'След колко дни да се повтори търсенето в Търговския регистър за обекти без открита фирма.',
  places_grid_max_calls:
    'Максимален брой заявки към Google Places при едно нощно сканиране на клетките.',
  harvest_retry_days:
    'След колко дни да се обнови проверката в каталози (Superdoc и др.) за един обект.',
  discovery_max_queries_per_run:
    'Максимален брой текстови заявки за търсене при едно нощно изпълнение на discovery етапа.',
  discovery_tier_cooldowns:
    'Базови дни за изчакване между пусканията на заявките по нива (core, core_area, service, service_area, intent, en).',
};

function BooleanSetting({
  settingKey,
  value,
  onChange,
}: {
  settingKey: string;
  value: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="adm__switch">
      <div className="adm__setting-head">
        <span className="adm__setting-title">{TITLES[settingKey] ?? settingKey}</span>
        <span className="adm__key">{settingKey}</span>
      </div>
      <div className="adm__switch-control">
        <input
          type="checkbox"
          className="adm__switch-input"
          checked={value}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span
          className={`adm__switch-badge ${value ? 'adm__switch-badge--on' : 'adm__switch-badge--off'}`}
        >
          {value ? 'ВКЛЮЧЕНО' : 'ИЗКЛЮЧЕНО'}
        </span>
      </div>
    </label>
  );
}

function JsonSetting({
  settingKey,
  value,
  onSave,
}: {
  settingKey: string;
  value: unknown;
  onSave: (next: unknown) => void;
}) {
  const [text, setText] = useState(JSON.stringify(value));
  const [error, setError] = useState<string | null>(null);

  return (
    <div className="adm__json">
      <div className="adm__setting-head">
        <span className="adm__setting-title">{TITLES[settingKey] ?? settingKey}</span>
        <span className="adm__key">{settingKey}</span>
      </div>
      <div className="adm__json-input-group">
        <input
          type="text"
          className="adm__json-input"
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            setError(null);
          }}
        />
        <button
          type="button"
          className="adm__json-btn"
          onClick={() => {
            try {
              onSave(JSON.parse(text) as unknown);
            } catch {
              setError('Невалиден JSON — например текст се пише в кавички: "gemini-2.5-flash"');
            }
          }}
        >
          Запази
        </button>
      </div>
      {error ? <span className="adm__err">{error}</span> : null}
    </div>
  );
}

interface LlmChainLane {
  provider: string;
  model: string;
  enabled: boolean;
}

const LLM_PROVIDERS = [
  { value: 'gemini-cli', label: 'Gemini CLI (Ultra - Local)' },
  { value: 'groq', label: 'Groq (Llama 3.3)' },
  { value: 'cloudflare', label: 'Cloudflare Workers AI' },
  { value: 'zai', label: 'Z.ai (GLM)' },
  { value: 'gemini', label: 'Google Gemini (Web API)' },
] as const;

function LlmChainSetting({
  settingKey,
  value,
  onSave,
}: {
  settingKey: string;
  value: unknown;
  onSave: (next: unknown) => void;
}) {
  const lanes = Array.isArray(value) ? (value as LlmChainLane[]) : [];
  const [selectedPrimary, setSelectedPrimary] = useState(lanes[0]?.provider ?? 'gemini-cli');
  const [jsonOpen, setJsonOpen] = useState(false);
  const [text, setText] = useState(JSON.stringify(value));
  const [error, setError] = useState<string | null>(null);

  const handlePrimaryChange = (provider: string) => {
    setSelectedPrimary(provider);
    const existing = lanes.find((l) => l.provider === provider);
    const rest = lanes.filter((l) => l.provider !== provider);
    const nextLanes = [
      existing ?? {
        provider,
        model: provider === 'gemini-cli' ? 'gemini-ultra' : 'default',
        enabled: true,
      },
      ...rest,
    ];
    onSave(nextLanes);
    setText(JSON.stringify(nextLanes));
  };

  return (
    <div className="adm__json">
      <div className="adm__setting-head">
        <span className="adm__setting-title">{TITLES[settingKey] ?? settingKey}</span>
        <span className="adm__key">{settingKey}</span>
      </div>

      <div
        className="adm__provider-select-group"
        style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginBottom: '0.75rem' }}
      >
        <label
          htmlFor="primary-llm-provider-select"
          style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--fg)' }}
        >
          Основен доставчик:
        </label>
        <select
          id="primary-llm-provider-select"
          className="filter-select"
          value={selectedPrimary}
          onChange={(e) => handlePrimaryChange(e.target.value)}
          aria-label="Основен AI доставчик"
        >
          {LLM_PROVIDERS.map((p) => (
            <option key={p.value} value={p.value}>
              {p.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn--secondary btn--sm"
          onClick={() => setJsonOpen(!jsonOpen)}
        >
          {jsonOpen ? 'Скрий JSON' : 'JSON детайли'}
        </button>
      </div>

      {jsonOpen && (
        <div className="adm__json-input-group">
          <input
            type="text"
            className="adm__json-input"
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setError(null);
            }}
          />
          <button
            type="button"
            className="adm__json-btn"
            onClick={() => {
              try {
                onSave(JSON.parse(text) as unknown);
              } catch {
                setError('Невалиден JSON');
              }
            }}
          >
            Запази
          </button>
        </div>
      )}
      {error ? <span className="adm__err">{error}</span> : null}
    </div>
  );
}

export function SettingsEditor() {
  const { data: settings = [], isLoading } = useSettings();
  const save = useSetSetting();

  if (isLoading) return <p className="adm__msg">Зареждане на настройки…</p>;

  const visibleSettings = settings.filter((setting) => setting.key in EXPLAIN);

  return (
    <div className="adm__settings-wrap">
      <div className="adm__section-header">
        <h3 className="adm__h">Конфигурация на системата</h3>
        <span className="adm__sub">
          Човешки разбираеми параметри на пайплайна — всички промени се записват в одитния журнал
        </span>
      </div>

      <ul className="adm__settings">
        {visibleSettings.map((setting) => (
          <li key={setting.key} className="adm__setting">
            {typeof setting.value === 'boolean' ? (
              <BooleanSetting
                settingKey={setting.key}
                value={setting.value}
                onChange={(next) => save.mutate({ key: setting.key, value: next })}
              />
            ) : setting.key === 'llm_chain' ? (
              <LlmChainSetting
                settingKey={setting.key}
                value={setting.value}
                onSave={(next) => save.mutate({ key: setting.key, value: next })}
              />
            ) : (
              <JsonSetting
                settingKey={setting.key}
                value={setting.value}
                onSave={(next) => save.mutate({ key: setting.key, value: next })}
              />
            )}
            {EXPLAIN[setting.key] ? <p className="adm__explain">{EXPLAIN[setting.key]}</p> : null}
          </li>
        ))}
      </ul>
      {save.isError ? <p className="adm__err">{(save.error as Error).message}</p> : null}
    </div>
  );
}
